import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import LanguageSwitchDialog from '../../components/common/LanguageSwitchDialog'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../utils/normLabels'
import { useAppDispatch, RootState } from '../../redux/store'
import { fetchSecurityCheckFamilyByCategory } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import {
  ListPageShell,
  listPageChipSx,
} from '../../components/common/ListPageShell'
import TableEmptyState from '../../components/common/TableEmptyState'
import i18n from '../../i18n'
import MappingFlowStepper from '../RelatedControls/MappingFlowStepper'
import '../RelatedControls/MappingFlow.css'
import './SecurityFramework.css'

const CheckIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    aria-hidden
  >
    <path d="M3.5 8.5l3 3 6-7" />
  </svg>
)

const ShieldIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <path d="M8 1.8l5.2 2v4.3c0 3.1-2.2 5.4-5.2 6.1-3-.7-5.2-3-5.2-6.1V3.8z" />
  </svg>
)

type ChipFilter = 'all' | 'current' | 'targets'

const SecurityFramework = () => {
  const { t, i18n: i18nApi } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [search, setSearch] = useState('')
  const [chip, setChip] = useState<ChipFilter>('all')
  const [openLanguageDialog, setOpenLanguageDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<{
    id: string
    path: string
  } | null>(null)

  const specs = useSelector(selectSpecifications)[0]
  const selectedNorm = specs?.NormBase || ''
  const availableNorms = specs?.IndustrialApplicableFrameworks || []
  const { SecurityCheckFamilyResponse } = useSelector(
    (state: RootState) => state.securitycheckFamily
  )

  useEffect(() => {
    if (!selectedNorm) return
    const action = fetchSecurityCheckFamilyByCategory({
      category: selectedNorm,
      page: 1,
      pageSize: 1,
      language: i18n.language.toLocaleUpperCase(),
    })
    if (action) dispatch(action)
  }, [dispatch, selectedNorm, i18n.language])

  const isIsoFamily = (norm: string) =>
    norm === 'ISO' || norm === 'ISO_13' || norm === 'ISO_22' || norm === 'NIST'

  const handleSelectNorm = (id: string) => {
    const isCurrentIsoOrNist = isIsoFamily(selectedNorm)
    const isSelectedIsoOrNist = isIsoFamily(id)
    const isMapping =
      isCurrentIsoOrNist && isSelectedIsoOrNist && id !== selectedNorm
    const isFrench = i18nApi.language === 'fr'

    if (isMapping && isFrench) {
      const path =
        id === selectedNorm ? `/security/${selectedNorm}` : `/norms/${id}`
      setPendingNavigation({ id, path })
      setOpenLanguageDialog(true)
    } else if (id === selectedNorm) {
      navigate(`/security/${selectedNorm}`)
    } else {
      navigate(`/norms/${id}`)
    }
  }

  const handleLanguageSwitchConfirm = () => {
    localStorage.setItem('i18nextLng', 'en')
    i18nApi.changeLanguage('en')
    setOpenLanguageDialog(false)
    if (pendingNavigation) {
      navigate(pendingNavigation.path)
      setPendingNavigation(null)
    }
  }

  const handleLanguageSwitchCancel = () => {
    setOpenLanguageDialog(false)
    setPendingNavigation(null)
  }

  const sourceLabel =
    t(`norms.${selectedNorm}.title`, {
      defaultValue: formatNormLabel(selectedNorm),
    }) || formatNormLabel(selectedNorm)

  const familiesLoaded =
    SecurityCheckFamilyResponse?.total ??
    SecurityCheckFamilyResponse?.totalPages ??
    '—'

  const targetsAvailable = availableNorms.filter((n) => n !== selectedNorm)
    .length

  const filteredNorms = useMemo(() => {
    const q = search.trim().toLowerCase()
    return availableNorms.filter((norm) => {
      if (chip === 'current' && norm !== selectedNorm) return false
      if (chip === 'targets' && norm === selectedNorm) return false
      if (!q) return true
      const title = t(`norms.${norm}.title`, {
        defaultValue: formatNormLabel(norm),
      }).toLowerCase()
      const desc = t(`norms.${norm}.description`, {
        defaultValue: '',
      }).toLowerCase()
      const key = norm.toLowerCase()
      return title.includes(q) || desc.includes(q) || key.includes(q)
    })
  }, [availableNorms, chip, search, selectedNorm, t])

  return (
    <div className="mapping-flow">
      <MappingFlowStepper currentStep={1} />

      <ListPageShell
        eyebrow={t('frameworkMappingPage.targetsEyebrow')}
        title={t('frameworkMappingPage.title')}
        titleTourId="frameworks-page-header"
        subtitle={t('frameworkMappingPage.subtitlePlain', {
          framework: formatNormLabel(selectedNorm),
          defaultValue: `You are currently working with ${formatNormLabel(selectedNorm)}. Select a target standard below to compare and align your controls with its structure and requirements.`,
        })}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          'frameworkMappingPage.filterStandards',
          'Filter standards'
        )}
        showFiltersChip={false}
        countLabel={t('listPages.standardsCount', {
          count: filteredNorms.length,
        })}
        chips={
          <>
            {(
              [
                ['all', t('listPages.all')],
                ['current', t('frameworkMappingPage.chipCurrent', 'Current')],
                [
                  'targets',
                  t('frameworkMappingPage.chipTargets', 'Targets'),
                ],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                style={listPageChipSx(chip === id)}
                onClick={() => setChip(id)}
              >
                {label}
              </button>
            ))}
          </>
        }
      >
        <div className="fw-shell-body">
          <div className="fw-source-bar">
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.sourceFramework', 'Source framework')}
              </div>
              <div className="fw-stat-value">{sourceLabel}</div>
            </div>
            <div className="fw-stat-divider" />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.familiesLoaded', 'Families loaded')}
              </div>
              <div className="fw-stat-value fw-mono">{familiesLoaded}</div>
            </div>
            <div className="fw-stat-divider" />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t(
                  'frameworkMappingPage.targetsAvailable',
                  'Targets available'
                )}
              </div>
              <div className="fw-stat-value fw-mono">{targetsAvailable}</div>
            </div>
            <button
              type="button"
              className="fw-ghost-btn"
              onClick={() => navigate('/settings')}
            >
              {t(
                'frameworkMappingPage.changeSource',
                'Change source framework'
              )}
            </button>
          </div>

          {filteredNorms.length === 0 ? (
            <div data-tour-id="mapping-catalog">
              <TableEmptyState />
            </div>
          ) : (
            <div className="fw-grid" data-tour-id="mapping-catalog">
              {filteredNorms.map((norm) => {
                const isCurrent = norm === selectedNorm
                return (
                  <div className="fw-card-slot" key={norm}>
                  <button
                    type="button"
                    className={`fw-card${isCurrent ? ' current' : ''}`}
                    onClick={() => handleSelectNorm(norm)}
                  >
                    <span className="fw-ic">
                      {isCurrent ? <CheckIcon /> : <ShieldIcon />}
                    </span>
                    <span className="fw-name">
                      {t(`norms.${norm}.title`, {
                        defaultValue: formatNormLabel(norm),
                      })}
                      {isCurrent && (
                        <em> {t('frameworkMappingPage.currentLabel')}</em>
                      )}
                    </span>
                    <span className="fw-desc">
                      {t(`norms.${norm}.description`, { defaultValue: '' })}
                    </span>
                    <span className="fw-cta">
                      {isCurrent
                        ? t('frameworkMappingPage.currentlyUsing')
                        : t('frameworkMappingPage.mapToThis')}
                    </span>
                  </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </ListPageShell>

      <LanguageSwitchDialog
        open={openLanguageDialog}
        onConfirm={handleLanguageSwitchConfirm}
        onCancel={handleLanguageSwitchCancel}
      />
    </div>
  )
}

export default SecurityFramework
